import Sentry from "../sentry/sentry";
import { getErrorMessage, isRecoverableNetworkError } from "./networkErrors";

let cleanupGlobalHandlers = null;

const normalizeThrowable = (value) => {
  if (value instanceof Error) return value;
  const message = getErrorMessage(value) || "Non-Error exception";
  const error = new Error(message);
  error.name = "NormalizedThrowable";
  error.originalValue = value;
  return error;
};

// Enhanced error reporting with more context
export const captureErrorWithContext = (error, context = {}) => {
  const normalizedError = normalizeThrowable(error);

  if (isRecoverableNetworkError(normalizedError) && !context.extra?.url) {
    return;
  }

  Sentry.withScope((scope) => {
    // Add custom context
    if (context.user) scope.setUser(context.user);
    if (context.tags) {
      Object.keys(context.tags).forEach((key) => {
        scope.setTag(key, context.tags[key]);
      });
    }
    if (context.extra) {
      Object.keys(context.extra).forEach((key) => {
        scope.setExtra(key, context.extra[key]);
      });
    }

    // Add breadcrumb for debugging
    scope.addBreadcrumb({
      message: `Error occurred: ${normalizedError.message}`,
      level: "error",
      data: {
        errorName: normalizedError.name,
        stack: normalizedError.stack,
        originalValue: normalizedError.originalValue,
        ...context.breadcrumbData,
      },
    });

    // Capture the exception
    Sentry.captureException(normalizedError);
  });
};

// Capture user action for debugging
export const captureUserAction = (action, data = {}) => {
  Sentry.addBreadcrumb({
    message: action,
    level: "info",
    category: "user-action",
    data,
  });
};

// Enhanced promise rejection handler
export const setupGlobalErrorHandlers = () => {
  if (cleanupGlobalHandlers) {
    return cleanupGlobalHandlers;
  }

  // Handle unhandled promise rejections
  const rejectionHandler = (event) => {
    console.error("Unhandled promise rejection:", event.reason);
    if (isRecoverableNetworkError(event.reason)) {
      event.preventDefault?.();
      return;
    }

    captureErrorWithContext(event.reason, {
      tags: { errorType: "unhandledPromiseRejection" },
      extra: { originalReason: event.reason },
    });
    event.preventDefault?.();
  };
  global.addEventListener?.("unhandledrejection", rejectionHandler);

  // Handle uncaught exceptions (for React Native)
  const previousGlobalHandler = global.ErrorUtils?.getGlobalHandler?.();
  global.ErrorUtils?.setGlobalHandler?.((error, isFatal) => {
    console.error("Global error handler:", error);
    if (isRecoverableNetworkError(error)) {
      return;
    }
    captureErrorWithContext(error, {
      tags: {
        errorType: "globalError",
        isFatal: isFatal ? "true" : "false",
      },
    });
    previousGlobalHandler?.(error, isFatal);
  });

  cleanupGlobalHandlers = () => {
    global.removeEventListener?.("unhandledrejection", rejectionHandler);
    if (previousGlobalHandler) {
      global.ErrorUtils?.setGlobalHandler?.(previousGlobalHandler);
    }
    cleanupGlobalHandlers = null;
  };

  return cleanupGlobalHandlers;
};

// Network error helper
export const captureNetworkError = (url, error, requestData = {}) => {
  captureErrorWithContext(error, {
    tags: { errorType: "networkError" },
    extra: {
      url,
      requestData,
      timestamp: new Date().toISOString(),
    },
    breadcrumbData: { url, method: requestData.method || "GET" },
  });
};
